import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import Button from 'src/components/Button';
import Chip from 'src/components/Chip';
import MenuPositionConfigurator from 'src/components/MenuPositionConfigurator';
import AddOrderPosition from 'src/components/AddOrderPosition';
import Image from 'next/image';

import theme from 'src/styles/components/MenuPosition.module.scss';
import { calculateVh } from 'src/hocs/withScreenHeightDetector';
import { ROUTE_TYPES_VALUES } from 'src/modules/Restaurant';

const MenuPosition = ({
    id,
    name,
    category,
    description,
    imageUrl,
    imageSrcSetUrls,
    price,
    isActive,
    context,
    tags,
    allergens,
    canAddPosition,
    menuId,
    categoryId,
    emitMessage,
    dismissFirstMessage,
    sessionError,
    isAddPositionDisabled
}) => {
    const intl = useIntl();
    const [isConfiguratorVisible, setConfiguratorVisibility] = useState(false);
    const [isCardClicked, setIsCardClicked] = useState(false);

    const handleOnMouseDown = (e) => {
        if (e.target.type !== 'button'
            && isActive
            && canAddPosition
            && !sessionError
            && !isAddPositionDisabled) {
            setIsCardClicked(true);
        }
    };

    const handleOnMouseUp = () => {
        setTimeout(() => setIsCardClicked(false), 200);
    };

    const handleOnMouseLeave = () => {
        setIsCardClicked(false);
    };

    const handleOpenConfigurator = (e) => {
        e.preventDefault();

        calculateVh();

        emitMessage({
            type: 'small',
            message: (
                <MenuPositionConfigurator
                  id={id}
                  name={name}
                  category={category}
                  description={description}
                  tags={tags}
                  allergens={allergens}
                  onAddPosition={dismissFirstMessage}
                  price={price}
                  menuId={menuId}
                  categoryId={categoryId}
                  imageUrl={imageUrl}
                  imageSrcSetUrls={imageSrcSetUrls}
                  isAddPositionPossible={isActive && canAddPosition && !isAddPositionDisabled && !sessionError}
                />
            ),
            className: theme.modal,
            hasNoHeader: true,
            actions: (
                <AddOrderPosition
                  isAddPositionPossible={isActive && canAddPosition && !isAddPositionDisabled && !sessionError}
                />
            ),
            isEscKeyDownEnabled: true,
            isDismissButtonEnabled: true,
            onDismiss: () => { setConfiguratorVisibility(false); }
        });
    };

    return (
        <div
          className={classnames(theme.cardContainer, { [theme.configuratorVisible]: isConfiguratorVisible })}
          categoryid={categoryId}
        >
            <div className={theme.cardContainerInner}>
                <div
                  className={classnames(
                    theme.card, { [theme.active]: !(!isActive
                        && (
                            context?.routeType === ROUTE_TYPES_VALUES.RESTAURANT
                            || context?.routeType === ROUTE_TYPES_VALUES.BILL_STATUS
                            || context?.routeType === ROUTE_TYPES_VALUES.BILL_WAIT
                            || (context?.routeType === ROUTE_TYPES_VALUES.MENU && context?.isRestaurantOpen))) },
                    { [theme.clicked]: isCardClicked }
                  )}
                  role="button"
                  onClick={handleOpenConfigurator}
                  onMouseDown={handleOnMouseDown}
                  onMouseLeave={handleOnMouseLeave}
                  onMouseUp={handleOnMouseUp}
                  tabIndex={0}
                >
                    {imageUrl && (
                        <div className={classnames(theme.photoContainer, { [theme.circle]: context?.menuPositionImageShape === 1 })}>
                            <Image
                              className={theme.photo}
                              src={imageUrl}
                              srcset={imageSrcSetUrls}
                              alt={name}
                              width={400}
                              height={200}
                              unoptimized="true"
                            />
                        </div>
                    )}
                    <div className={theme.container}>
                        <div className={theme.content}>
                            <h3 className={theme.name}>
                                {name}
                            </h3>
                            {context?.showDescription && (
                                <div className={theme.description}>
                                    {description}
                                </div>
                            )}
                            {allergens && context?.showAllergens && (
                                <div className={theme.allergens}>
                                    <b>{intl.formatMessage({ id: 'App.allergens' })}:</b> {allergens.join(', ')}
                                </div>
                            )}
                            {tags && context?.showTags && (
                                <div className={theme.tags}>
                                    {tags.map((tag) => <Chip key={tag}>{tag}</Chip>)}
                                </div>
                            )}
                        </div>
                        <div className={theme.order}>
                            <div className={theme.price}>
                                {intl.formatNumber(price, { style: 'currency', currency: 'PLN' })}
                            </div>
                            {(isActive && canAddPosition && !sessionError) && (
                                <Button
                                  disabled={isAddPositionDisabled}
                                  floating
                                  mini
                                  iconSmall
                                  accent
                                  icon="icon-plus"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

MenuPosition.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    imageSrcSetUrls: PropTypes.string,
    price: PropTypes.number.isRequired,
    isActive: PropTypes.bool,
    context: PropTypes.shape({
        tableId: PropTypes.string,
        restaurantId: PropTypes.string,
        routeType: PropTypes.number,
        isRestaurantOpen: PropTypes.bool,
        menuPositionImageShape: PropTypes.number,
        showAllergens: PropTypes.bool,
        showTags: PropTypes.bool,
        showDescription: PropTypes.bool
    }),
    tags: PropTypes.arrayOf(PropTypes.string),
    allergens: PropTypes.arrayOf(PropTypes.string),
    emitMessage: PropTypes.func.isRequired,
    dismissFirstMessage: PropTypes.func.isRequired,
    canAddPosition: PropTypes.bool.isRequired,
    menuId: PropTypes.string.isRequired,
    categoryId: PropTypes.string.isRequired,
    sessionError: PropTypes.bool.isRequired,
    isAddPositionDisabled: PropTypes.bool.isRequired
};

MenuPosition.defaultProps = {
    isActive: true
};

export default MenuPosition;
